import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { Link } from 'react-router-dom';
import { ErrorState, EmptyState } from '@/components/feature/AsyncState';
import { useCustomers } from '@/hooks/useCustomers';
import { toggleCustomerActive } from '@/services/customers';
import CustomerModal from './components/CustomerModal';
import type { Customer } from '@/services/customers';

type CustomerFilter = 'all' | 'active' | 'inactive';

export default function CustomersPage() {
  const { data: customers, loading, error, refetch } = useCustomers();
  const [showNew, setShowNew] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [filter, setFilter] = useState<CustomerFilter>('all');
  const [togglingId, setTogglingId] = useState('');

  const activeCount = customers.filter((c) => c.isActive).length;
  const inactiveCount = customers.length - activeCount;

  const filtered = customers.filter((c) => {
    if (filter === 'active') return c.isActive;
    if (filter === 'inactive') return !c.isActive;
    return true;
  });

  async function handleToggle(id: string, current: boolean) {
    setTogglingId(id);
    await toggleCustomerActive(id, !current);
    setTogglingId('');
    refetch();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Clientes</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{customers.length} clientes registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nuevo Cliente
          </button>
        </div>

        {/* Filter */}
        <div className="inline-flex items-center gap-1 p-1 bg-background-100 rounded-full">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer font-medium ${filter === 'all' ? 'bg-white text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            Todos ({customers.length})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer font-medium ${filter === 'active' ? 'bg-white text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            Activos ({activeCount})
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer font-medium ${filter === 'inactive' ? 'bg-white text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            Inactivos ({inactiveCount})
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-background-200 p-5 animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-background-100"></div>
                  <div className="flex-1">
                    <div className="h-3 bg-background-100 rounded w-2/3 mb-2"></div>
                    <div className="h-3 bg-background-100 rounded w-1/3"></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="h-8 bg-background-100 rounded"></div>
                  <div className="h-8 bg-background-100 rounded"></div>
                  <div className="h-8 bg-background-100 rounded"></div>
                  <div className="h-8 bg-background-100 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((customer) => {
                return (
                  <div key={customer.id} className="bg-white rounded-xl border border-background-200 p-5 hover:border-accent-300 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-accent-600 font-bold text-sm">
                            {customer.firstName[0] || '?'}{customer.lastName[0] || ''}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900">
                            {customer.firstName} {customer.lastName}
                          </h3>
                          <p className="text-xs text-foreground-500">{customer.city || 'Sin ciudad'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {customer.originChannel === 'meta_ads' && (
                          <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium whitespace-nowrap">Meta Ads</span>
                        )}
                        {!customer.isActive && (
                          <span className="text-[10px] px-2 py-0.5 bg-background-100 text-foreground-500 rounded-full font-medium whitespace-nowrap">Inactivo</span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-foreground-500">Total Comprado</p>
                        <p className="font-semibold text-foreground-950">${customer.totalPurchases.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">Compras</p>
                        <p className="font-semibold text-foreground-950">{customer.purchaseCount}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">Ticket Promedio</p>
                        <p className="font-semibold text-foreground-950">${customer.averageTicket.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">Última Compra</p>
                        <p className="font-semibold text-foreground-950 text-xs">{customer.lastPurchase ? new Date(customer.lastPurchase).toLocaleDateString('es-CO') : '—'}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-background-100 flex items-center gap-2">
                      <button onClick={() => setEditingCustomer(customer)} className="flex-1 text-xs py-2 rounded-lg border border-background-200 text-foreground-600 hover:bg-background-50 transition-colors text-center whitespace-nowrap cursor-pointer">
                        <i className="ri-pencil-line mr-1"></i>Editar
                      </button>
                      <Link to="/admin/pedidos" className="flex-1 text-xs py-2 rounded-lg bg-accent-50 text-accent-600 hover:bg-accent-100 transition-colors text-center whitespace-nowrap font-medium">
                        <i className="ri-shopping-bag-3-line mr-1"></i>Pedidos ({customer.purchaseCount})
                      </Link>
                      <button
                        onClick={() => handleToggle(customer.id, customer.isActive)}
                        disabled={togglingId === customer.id}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0"
                        title={customer.isActive ? 'Desactivar' : 'Activar'}
                      >
                        <i className={`${customer.isActive ? 'ri-toggle-line text-emerald-600' : 'ri-toggle-fill text-foreground-400'} text-lg`}></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {filtered.length === 0 && customers.length > 0 && (
              <div className="text-center py-12 text-foreground-500">
                <i className="ri-user-search-line text-3xl block mb-2"></i>
                <p className="text-sm">No hay clientes con ese filtro.</p>
              </div>
            )}
          </>
        )}
      </div>

      {!loading && !error && customers.length === 0 && <EmptyState />}

      {showNew && <CustomerModal onClose={() => setShowNew(false)} onSaved={refetch} />}
      {editingCustomer && <CustomerModal customer={editingCustomer} onClose={() => setEditingCustomer(null)} onSaved={refetch} />}
    </AdminLayout>
  );
}